import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  BookOpen,
  Trophy,
  User,
  LogOut,
  ChevronLeft,
  Save,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { t, language, changeLanguage } = useLanguage();
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    preferred_language: user?.preferred_language || language
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/settings`, formData, { withCredentials: true });
      updateUser(formData);
      changeLanguage(formData.preferred_language);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-card border-r border-border hidden lg:block">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_egns-deutsch/artifacts/wljgvqe2_copy_DF94F79F-AD21-40BD-8A6F-78D3B8D2676A.jpeg" 
              alt="EGNS Logo" 
              className="h-10 w-auto"
            />
          </div>

          <nav className="space-y-2">
            <Link
              to="/dashboard"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
            >
              <LayoutDashboard className="h-5 w-5" />
              {t('dashboard')}
            </Link>
            <Link
              to="/lessons"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
            >
              <BookOpen className="h-5 w-5" />
              {t('lessons')}
            </Link>
            <Link
              to="/achievements"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
            >
              <Trophy className="h-5 w-5" />
              {t('achievements')}
            </Link>
            <Link
              to="/profile"
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary/10 text-primary font-medium"
            >
              <User className="h-5 w-5" />
              {t('profile')}
            </Link>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
          >
            <LogOut className="h-5 w-5" />
            {t('logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pb-20 lg:pb-0">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">{t('profile')}</h1>
                <p className="text-muted-foreground text-sm">{t('settings')}</p>
              </div>
            </div>
            <LanguageSelector />
          </div>
        </header>

        {/* Content */}
        <div className="p-6 max-w-2xl">
          {/* Profile Card */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="font-heading text-xl font-semibold">{user?.name}</h2>
                  <p className="text-muted-foreground">{user?.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-1 rounded-full bg-teal-500/10 text-teal-600 text-xs font-medium">
                      {user?.current_level || 'A1'}
                    </span>
                    <span className="px-2 py-1 rounded-full bg-amber-500/10 text-amber-600 text-xs font-medium">
                      {user?.total_points || 0} {t('points')}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle>{t('settings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">{t('name')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="profile-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('language')}</Label>
                <Select 
                  value={formData.preferred_language} 
                  onValueChange={(value) => setFormData({ ...formData, preferred_language: value })}
                >
                  <SelectTrigger data-testid="profile-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">🇬🇧 English</SelectItem>
                    <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                    <SelectItem value="ar">🇪🇬 العربية</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="w-full bg-teal-500 hover:bg-teal-600"
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {t('saveChanges')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border lg:hidden">
        <div className="grid grid-cols-4 gap-1 p-2">
          <Link
            to="/dashboard"
            className="flex flex-col items-center gap-1 py-2 text-muted-foreground"
          >
            <LayoutDashboard className="h-5 w-5" />
            <span className="text-xs">{t('dashboard')}</span>
          </Link>
          <Link
            to="/lessons"
            className="flex flex-col items-center gap-1 py-2 text-muted-foreground"
          >
            <BookOpen className="h-5 w-5" />
            <span className="text-xs">{t('lessons')}</span>
          </Link>
          <Link
            to="/achievements"
            className="flex flex-col items-center gap-1 py-2 text-muted-foreground"
          >
            <Trophy className="h-5 w-5" />
            <span className="text-xs">{t('achievements')}</span>
          </Link>
          <Link
            to="/profile"
            className="flex flex-col items-center gap-1 py-2 text-primary"
          >
            <User className="h-5 w-5" />
            <span className="text-xs">{t('profile')}</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
